import type { AdmissionsAIContextualAction, AdmissionsCitation } from './types';
import {
  resolveCitationEntityLabel,
  resolveCitationRouteLabel,
  resolveCitationRouteTarget,
  type CitationRouteTarget,
} from './citation-routing';

const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^)]+)\)/g;

export type AssistantLinkGroupKey = 'applications' | 'families' | 'settings' | 'people' | 'related';

export type AssistantLinkRow = {
  id: string;
  href: string;
  label: string;
  groupKey: AssistantLinkGroupKey;
  groupLabel: string;
  meta: string | null;
  preview: string | null;
  title: string | null;
  external: boolean;
};

export type AssistantLinkGroup = {
  key: AssistantLinkGroupKey;
  label: string;
  items: AssistantLinkRow[];
};

export type AssistantSourceRow = {
  id: string;
  label: string;
  table: string;
  fieldPath: string;
  preview: string | null;
  applicationCode: string | null;
  title: string | null;
};

export type AssistantResultCardAction = {
  href: string;
  label: string;
};

export type AssistantResultCard = {
  id: string;
  label: string;
  meta: string | null;
  preview: string | null;
  primaryAction: AssistantResultCardAction;
  secondaryAction: AssistantResultCardAction | null;
};

type InlineLinkRow = {
  href: string;
  label: string;
};

type AssistantResponseSections = {
  mainText: string;
  explanationText: string | null;
};

const normalizeText = (value: unknown): string => String(value ?? '').replace(/\s+/g, ' ').trim();
const EXPLANATION_BLOCK_PATTERN = /\b(deterministic method projection|selected sources:|retrieval budget limits|coverage is constrained|coverage is limited to selected sources)\b/i;
const APPLICATION_CODE_PATTERN = /\b(?:APP|FA)-[A-Z0-9]{2,}(?:-[A-Z0-9]{2,})*\b/gi;

const isSafeHref = (href: string): boolean => (
  href.startsWith('/') || /^https?:\/\//i.test(href)
);

const isExternalHref = (href: string): boolean => /^https?:\/\//i.test(href);

const buildLinkId = (href: string, label: string): string => `${href}::${label}`.toLowerCase();

const stripCitationPreviewCodes = (preview: string, applicationCode?: string | null): string => {
  let next = preview.replace(APPLICATION_CODE_PATTERN, ' ');
  if (applicationCode) {
    const escapedCode = applicationCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    next = next.replace(new RegExp(escapedCode, 'ig'), ' ');
  }
  return next
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const inferGroupFromHref = (href: string, label: string): AssistantLinkGroupKey => {
  const normalizedHref = href.toLowerCase();
  const normalizedLabel = label.toLowerCase();

  if (
    normalizedHref.includes('/school/financialaid/')
    || normalizedHref.includes('/school/financial-aid/')
    || normalizedHref.includes('/school/admissions/children/')
    || normalizedHref.includes('/school/admissions/family/')
    || normalizedHref.includes('/school/admissions/documents/')
    || normalizedHref.includes('/school/admissions/notes/')
    || normalizedHref.includes('/school/admissions/tuition/')
    || normalizedLabel.includes('application')
  ) {
    return 'applications';
  }
  if (
    normalizedHref.includes('/school/students/family/')
    || normalizedHref.includes('/parent/portal')
    || normalizedLabel.includes('family')
  ) {
    return 'families';
  }
  if (normalizedHref.includes('/school/settings/')) {
    return 'settings';
  }
  if (normalizedHref.includes('/users') || normalizedHref.includes('/faculty') || normalizedLabel.includes('staff')) {
    return 'people';
  }
  return 'related';
};

const GROUP_LABELS: Record<AssistantLinkGroupKey, string> = {
  applications: 'Applications',
  families: 'Families',
  settings: 'Settings',
  people: 'People',
  related: 'Related',
};

const getActionPayload = (action: AdmissionsAIContextualAction): Record<string, unknown> => (
  action.payload && typeof action.payload === 'object' ? action.payload : {}
);

const resolveActionRoute = (action: AdmissionsAIContextualAction): string | null => {
  const payload = getActionPayload(action);
  return normalizeText(action.targetRoute) || normalizeText(payload.targetRoute) || normalizeText(payload.href) || null;
};

const resolveActionEntityLabel = (action: AdmissionsAIContextualAction): string | null => {
  const payload = getActionPayload(action);
  return (
    normalizeText(action.entityRef?.label)
    || normalizeText(payload.entityLabel)
    || normalizeText(action.label)
    || null
  );
};

const isHumanReadableActionLabel = (value: unknown): boolean => {
  const label = normalizeText(value);
  if (!label) return false;
  if (/\b[A-Z]{2,10}-[A-Z0-9]{2,12}(?:-[A-Z0-9]{2,12})+\b/i.test(label.toUpperCase())) {
    return false;
  }
  return /[A-Za-z]/.test(label);
};

const sanitizeActionLabel = (value: unknown): string => normalizeText(value)
  .replace(/\b[A-Z]{2,10}-[A-Z0-9]{2,12}(?:-[A-Z0-9]{2,12})+\b/gi, '')
  .replace(/\brecord\b/ig, '')
  .replace(/\s+/g, ' ')
  .trim();

const isThoughtfulActionLabel = (value: unknown): boolean => {
  const label = sanitizeActionLabel(value);
  if (!label) return false;
  if (!isHumanReadableActionLabel(label)) return false;
  if (/\b(identify|estimate|forecast|summarize|analy[sz]e|calculate|compare|query|count)\b/i.test(label)) {
    return false;
  }
  return true;
};

export const selectRecommendedAssistantActions = (
  actions: AdmissionsAIContextualAction[] | undefined,
  limit = 4,
): AdmissionsAIContextualAction[] => {
  const selected: AdmissionsAIContextualAction[] = [];
  const seen = new Set<string>();
  for (const action of actions ?? []) {
    const label = sanitizeActionLabel(action.label);
    if (!label || !isThoughtfulActionLabel(label)) {
      continue;
    }
    const key = label.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    selected.push({
      ...action,
      label,
    });
    if (selected.length >= limit) {
      break;
    }
  }
  return selected;
};

export const stripInlineAssistantLinks = (
  text: string,
): { sanitizedText: string; inlineLinks: InlineLinkRow[] } => {
  const inlineLinks: InlineLinkRow[] = [];
  const sanitizedText = text.replace(MARKDOWN_LINK_PATTERN, (fullMatch, rawLabel, rawHref) => {
    const label = normalizeText(rawLabel);
    const href = normalizeText(rawHref);
    if (!label) {
      return '';
    }
    if (!href || !isSafeHref(href)) {
      return label;
    }
    inlineLinks.push({ href, label });
    return label;
  });
  return { sanitizedText, inlineLinks };
};

export const splitAssistantResponseSections = (text: string): AssistantResponseSections => {
  const normalized = String(text ?? '').trim();
  if (!normalized) {
    return { mainText: '', explanationText: null };
  }
  const blocks = normalized
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter((block) => block.length > 0);
  const explanationBlocks = blocks.filter((block) => EXPLANATION_BLOCK_PATTERN.test(block));
  if (explanationBlocks.length === 0) {
    return { mainText: normalized, explanationText: null };
  }
  const mainBlocks = blocks.filter((block) => !EXPLANATION_BLOCK_PATTERN.test(block));
  return {
    mainText: mainBlocks.join('\n\n').trim(),
    explanationText: explanationBlocks.join('\n\n').trim() || null,
  };
};

const buildCitationMeta = (citation: AdmissionsCitation, target: CitationRouteTarget, label: string): string | null => {
  if (resolveCitationEntityLabel(citation)) {
    if (target.kind === 'application') return 'Open application';
    if (target.kind === 'family') return 'Open family details';
    if (target.kind === 'student') return 'Open student details';
    return 'Open resource';
  }
  if (/^open /i.test(label)) {
    return null;
  }
  return target.kind === 'application'
    ? 'Application route'
    : target.kind === 'family'
      ? 'Family details'
      : target.kind === 'student'
        ? 'Student details'
        : 'Resource';
};

const buildCitationPreview = (citation: AdmissionsCitation, label: string): string | null => {
  const preview = normalizeText(citation.preview);
  if (!preview) {
    return null;
  }
  const sanitized = stripCitationPreviewCodes(preview, normalizeText(citation.applicationCode) || null);
  if (!sanitized || sanitized.toLowerCase() === label.toLowerCase()) {
    return null;
  }
  return sanitized;
};

const buildLinkTitle = (input: {
  label: string;
  href: string;
  meta: string | null;
  preview: string | null;
}): string | null => {
  const parts = [input.label, input.meta, input.preview, input.href]
    .map((value) => normalizeText(value))
    .filter((value) => value.length > 0);
  return parts.length > 0 ? parts.join('\n') : null;
};

const sanitizeAssistantCardLabel = (value: unknown): string => normalizeText(value)
  .replace(/^open\s+/i, '')
  .replace(/\b(?:APP|FA)-[A-Z0-9]{2,}(?:-[A-Z0-9]{2,})*\b/gi, '')
  .replace(/\s+/g, ' ')
  .trim();

const resolveAssistantActionPreview = (action: AdmissionsAIContextualAction): string | null => {
  const payload = getActionPayload(action);
  return normalizeText(payload.summary) || normalizeText(payload.previewText) || null;
};

const resolveAssistantActionMeta = (action: AdmissionsAIContextualAction): string | null => {
  const payload = getActionPayload(action);
  const parts = [
    normalizeText(payload.applicationCode),
    normalizeText(payload.statusLabel),
  ].filter((value) => value.length > 0);
  return parts.length > 0 ? parts.join(' • ') : null;
};

const resolveResultCardActionLabel = (href: string): string => {
  const normalizedHref = href.toLowerCase();
  if (normalizedHref.includes('/school/admissions/family/') || normalizedHref.includes('/school/students/family/')) {
    return 'Open family details';
  }
  return 'Open application';
};

const findSupportingCitation = (input: {
  citations: AdmissionsCitation[];
  href: string;
  label: string;
  applicationCode?: string | null;
}): AdmissionsCitation | null => {
  const normalizedLabel = normalizeText(input.label).toLowerCase();
  const normalizedCode = normalizeText(input.applicationCode).toLowerCase();
  for (const citation of input.citations) {
    const target = resolveCitationRouteTarget(citation);
    if (target?.href === input.href) {
      return citation;
    }
    if (normalizedCode && normalizeText(citation.applicationCode).toLowerCase() === normalizedCode) {
      return citation;
    }
    if (normalizedLabel && normalizeText(resolveCitationEntityLabel(citation)).toLowerCase() === normalizedLabel) {
      return citation;
    }
  }
  return null;
};

export const buildAssistantLinkGroups = (input: {
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[] | undefined;
  inlineLinks: InlineLinkRow[];
}): AssistantLinkGroup[] => {
  const links = new Map<string, AssistantLinkRow>();

  input.citations.forEach((citation, index) => {
    const target = resolveCitationRouteTarget(citation);
    if (!target || !isSafeHref(target.href)) {
      return;
    }
    const label = normalizeText(resolveCitationRouteLabel(citation, target)) || `Link ${index + 1}`;
    const groupKey = inferGroupFromHref(target.href, label);
    const preview = buildCitationPreview(citation, label);
    const meta = buildCitationMeta(citation, target, label);
    const row: AssistantLinkRow = {
      id: buildLinkId(target.href, label),
      href: target.href,
      label,
      groupKey,
      groupLabel: GROUP_LABELS[groupKey],
      meta,
      preview,
      title: buildLinkTitle({ label, href: target.href, meta, preview }),
      external: isExternalHref(target.href),
    };
    links.set(target.href, links.get(target.href) ?? row);
  });

  (input.actions ?? []).forEach((action, index) => {
    const href = resolveActionRoute(action);
    if (!href || !isSafeHref(href)) {
      return;
    }
    const label = resolveActionEntityLabel(action) || normalizeText(action.label) || `Action link ${index + 1}`;
    const groupKey = inferGroupFromHref(href, label);
    const meta = [
      normalizeText(action.actionCategory),
      normalizeText(action.approvalCategory),
    ].filter((value) => value.length > 0).join(' • ') || null;
    const preview = normalizeText(action.mutationOperation) || null;
    const row: AssistantLinkRow = {
      id: buildLinkId(href, label),
      href,
      label,
      groupKey,
      groupLabel: GROUP_LABELS[groupKey],
      meta,
      preview,
      title: buildLinkTitle({ label, href, meta, preview }),
      external: isExternalHref(href),
    };
    links.set(href, links.get(href) ?? row);
  });

  input.inlineLinks.forEach((link, index) => {
    const href = normalizeText(link.href);
    const label = normalizeText(link.label) || `Related link ${index + 1}`;
    if (!href || !label || !isSafeHref(href)) {
      return;
    }
    const groupKey = inferGroupFromHref(href, label);
    const row: AssistantLinkRow = {
      id: buildLinkId(href, label),
      href,
      label,
      groupKey,
      groupLabel: GROUP_LABELS[groupKey],
      meta: null,
      preview: null,
      title: buildLinkTitle({ label, href, meta: null, preview: null }),
      external: isExternalHref(href),
    };
    links.set(href, links.get(href) ?? row);
  });

  const order: AssistantLinkGroupKey[] = ['applications', 'families', 'settings', 'people', 'related'];
  return order
    .map((groupKey) => {
      const items = [...links.values()]
        .filter((link) => link.groupKey === groupKey)
        .sort((left, right) => left.label.localeCompare(right.label));
      if (items.length === 0) {
        return null;
      }
      return {
        key: groupKey,
        label: GROUP_LABELS[groupKey],
        items,
      } satisfies AssistantLinkGroup;
    })
    .filter((group): group is AssistantLinkGroup => Boolean(group));
};

export const buildAssistantResultCards = (input: {
  citations: AdmissionsCitation[];
  actions: AdmissionsAIContextualAction[] | undefined;
}): AssistantResultCard[] => {
  const cards = new Map<string, AssistantResultCard>();

  (input.actions ?? []).forEach((action) => {
    const href = resolveActionRoute(action);
    if (!href || !isSafeHref(href)) {
      return;
    }
    const rawLabel = resolveActionEntityLabel(action);
    const label = sanitizeAssistantCardLabel(rawLabel) || normalizeText(rawLabel) || 'Application';
    const payload = getActionPayload(action);
    const citation = findSupportingCitation({
      citations: input.citations,
      href,
      label,
      applicationCode: normalizeText(payload.applicationCode) || null,
    });
    const preview = resolveAssistantActionPreview(action)
      || (citation ? buildCitationPreview(citation, label) : null);
    const meta = resolveAssistantActionMeta(action)
      || (citation ? normalizeText(citation.applicationCode) || null : null);
    cards.set(href, {
      id: buildLinkId(href, label),
      label,
      meta,
      preview,
      primaryAction: {
        href,
        label: resolveResultCardActionLabel(href),
      },
      secondaryAction: null,
    });
  });

  input.citations.forEach((citation) => {
    const target = resolveCitationRouteTarget(citation);
    if (!target || !isSafeHref(target.href) || cards.has(target.href)) {
      return;
    }
    const label = sanitizeAssistantCardLabel(resolveCitationEntityLabel(citation))
      || normalizeText(resolveCitationRouteLabel(citation, target))
      || 'Application';
    cards.set(target.href, {
      id: buildLinkId(target.href, label),
      label,
      meta: normalizeText(citation.applicationCode) || null,
      preview: buildCitationPreview(citation, label),
      primaryAction: {
        href: target.href,
        label: resolveResultCardActionLabel(target.href),
      },
      secondaryAction: null,
    });
  });

  return [...cards.values()].slice(0, 5);
};

export const buildAssistantSources = (citations: AdmissionsCitation[]): AssistantSourceRow[] => (
  citations.map((citation, index) => {
    const applicationCode = normalizeText(citation.applicationCode) || null;
    const preview = normalizeText(citation.preview) || null;
    const label = applicationCode || `Source ${index + 1}`;
    const title = [label, normalizeText(citation.table), normalizeText(citation.fieldPath), preview]
      .filter((value): value is string => Boolean(value && value.length > 0))
      .join('\n') || null;
    return {
      id: `${normalizeText(citation.table)}:${normalizeText(citation.fieldPath)}:${index + 1}`,
      label,
      table: normalizeText(citation.table) || 'Source',
      fieldPath: normalizeText(citation.fieldPath) || 'field',
      preview,
      applicationCode,
      title,
    };
  })
);

export const canOpenAllAssistantLinks = (links: readonly AssistantLinkRow[]): boolean => {
  if (links.length < 2 || links.length > 3) {
    return false;
  }
  if (links.some((link) => link.external)) {
    return false;
  }
  return links.every((link) => link.groupKey === 'applications');
};
